import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Save, X, Calendar, Clock, User, MessageSquare, AlertCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

// Contact types that should NOT trigger the "Move status to Contacted?" suggestion,
// because they don't represent a successful, completed contact.
const NON_CONTACT_TYPES = new Set(["No Answer / Voicemail"]);

function todayIsoDate() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function nowHm() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fmtDateTime(date, time) {
  if (!date) return "—";
  try {
    const d = new Date(`${date}T${time || "00:00"}`);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return `${date} ${time || ""}`.trim();
  }
}

const BLANK = {
  date: "",
  time: "",
  staff_name: "",
  contact_type: "Initial Call",
  note: "",
  follow_up_needed: false,
  follow_up_date: "",
  areas_of_support: [],
};

const MAX_AREAS = 3;

export default function ActivityLog({ requestId, detail, currentUser, onUpdated, onStatusSuggest }) {
  const [contactTypes, setContactTypes] = useState([]);
  const [areasOfSupport, setAreasOfSupport] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    api
      .get("/admin/support-requests/contact-types")
      .then((r) => { if (!ignore) setContactTypes(r.data || []); })
      .catch(() => {});
    api
      .get("/admin/support-requests/areas-of-support")
      .then((r) => { if (!ignore) setAreasOfSupport(r.data || []); })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);

  const activities = useMemo(() => (detail?.activities || []).slice().sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)), [detail]);

  const summary = useMemo(() => {
    const total = activities.length;
    const last = activities.length ? activities[activities.length - 1] : null;
    const upcoming = activities
      .filter((a) => a.follow_up_needed && a.follow_up_date)
      .sort((a, b) => a.follow_up_date.localeCompare(b.follow_up_date));
    const nextFu = upcoming.find((a) => a.follow_up_date >= todayIsoDate()) || upcoming[0] || null;
    return { total, last, nextFu };
  }, [activities]);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...BLANK,
      date: todayIsoDate(),
      time: nowHm(),
      staff_name: currentUser?.name || currentUser?.email || "",
    });
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      date: a.date || "",
      time: a.time || "",
      staff_name: a.staff_name || "",
      contact_type: a.contact_type || "Initial Call",
      note: a.note || "",
      follow_up_needed: !!a.follow_up_needed,
      follow_up_date: a.follow_up_date || "",
      areas_of_support: Array.isArray(a.areas_of_support) ? a.areas_of_support : [],
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(BLANK);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time || !form.staff_name.trim() || !form.contact_type) {
      toast.error("Date, time, staff, and contact type are required.");
      return;
    }
    if (form.follow_up_needed && !form.follow_up_date) {
      toast.error("Follow-up date is required when follow-up is needed.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        time: form.time,
        staff_name: form.staff_name.trim(),
        contact_type: form.contact_type,
        note: form.note || "",
        follow_up_needed: !!form.follow_up_needed,
        follow_up_date: form.follow_up_needed ? form.follow_up_date : null,
        areas_of_support: form.areas_of_support || [],
      };
      let res;
      if (editingId) {
        res = await api.patch(`/admin/support-requests/${requestId}/activities/${editingId}`, payload);
        toast.success("Activity updated");
      } else {
        res = await api.post(`/admin/support-requests/${requestId}/activities`, payload);
        toast.success("Activity added");
        if (
          onStatusSuggest &&
          !NON_CONTACT_TYPES.has(payload.contact_type) &&
          (detail?.status === "new" || detail?.status === "in_progress")
        ) {
          onStatusSuggest();
        }
      }
      onUpdated?.(res.data);
      closeForm();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="activity-log" className="mt-8 border-t border-slate-200 pt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg tracking-tight text-slate-900">Chaplain Activity Log</h3>
          <p className="text-xs text-slate-500 mt-0.5">Confidential CCCHAPS documentation — never visible to employers.</p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={openCreate}
            data-testid="activity-add-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-xs font-semibold uppercase tracking-wider"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.2} /> Add Contact
          </button>
        )}
      </div>

      {/* Summary tiles */}
      <div data-testid="activity-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <SummaryTile label="Total Contacts" value={summary.total} icon={MessageSquare} testId="summary-total" />
        <SummaryTile
          label="Last Contact"
          value={summary.last ? fmtDateTime(summary.last.date, summary.last.time) : "—"}
          icon={Clock}
          testId="summary-last"
          small
        />
        <SummaryTile
          label="Next Follow-Up"
          value={summary.nextFu?.follow_up_date || "—"}
          icon={Calendar}
          testId="summary-next-fu"
          highlight={!!summary.nextFu && summary.nextFu.follow_up_date >= todayIsoDate()}
          small
        />
        <SummaryTile
          label="Current Status"
          value={(detail?.status || "").replace("_", " ")}
          icon={CheckCircle2}
          testId="summary-status"
          small
        />
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <form
          onSubmit={submit}
          data-testid="activity-form"
          className="mb-5 rounded-xl border border-[#4A6FA5]/30 bg-[#F0F4F8]/40 p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.18em] text-[#3B597B] font-semibold">
              {editingId ? "Edit contact" : "Record a new contact"}
            </p>
            <button
              type="button"
              onClick={closeForm}
              data-testid="activity-form-cancel"
              className="text-slate-500 hover:text-slate-800"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <FormField label="Date">
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
                data-testid="activity-date"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
              />
            </FormField>
            <FormField label="Time">
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                required
                data-testid="activity-time"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
              />
            </FormField>
          </div>
          <FormField label="Chaplain / Staff Member" hint="Auto-filled with logged-in staff — override if logging on behalf of another chaplain.">
            <input
              type="text"
              value={form.staff_name}
              onChange={(e) => setForm({ ...form, staff_name: e.target.value })}
              required
              data-testid="activity-staff"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
            />
          </FormField>
          <FormField label="Contact / Service Type">
            <select
              value={form.contact_type}
              onChange={(e) => setForm({ ...form, contact_type: e.target.value })}
              required
              data-testid="activity-contact-type"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] bg-white"
            >
              {(contactTypes.length ? contactTypes : [form.contact_type]).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Areas of Support" hint={`Optional. Select up to ${MAX_AREAS} that apply. Structured tags only — do not describe details here.`}>
            <div data-testid="activity-areas" className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {(areasOfSupport.length ? areasOfSupport : form.areas_of_support).map((area) => {
                const checked = form.areas_of_support.includes(area);
                const atLimit = !checked && form.areas_of_support.length >= MAX_AREAS;
                return (
                  <label key={area} className={`inline-flex items-center gap-2 text-sm ${atLimit ? "text-slate-400" : "text-slate-700"}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={atLimit}
                      data-testid={`activity-area-${area.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (form.areas_of_support.length >= MAX_AREAS) {
                            toast.error(`Select up to ${MAX_AREAS} areas of support.`);
                            return;
                          }
                          setForm({ ...form, areas_of_support: [...form.areas_of_support, area] });
                        } else {
                          setForm({ ...form, areas_of_support: form.areas_of_support.filter((a) => a !== area) });
                        }
                      }}
                      className="rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]"
                    />
                    {area}
                  </label>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">{form.areas_of_support.length} of {MAX_AREAS} selected</p>
          </FormField>
          <FormField label="Brief Internal Note" hint="Confidential — not visible to employers.">
            <textarea
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={3}
              maxLength={4000}
              data-testid="activity-note"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] resize-none"
            />
          </FormField>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.follow_up_needed}
                onChange={(e) => setForm({ ...form, follow_up_needed: e.target.checked })}
                data-testid="activity-followup-needed"
                className="rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]"
              />
              Follow-up needed
            </label>
            {form.follow_up_needed && (
              <input
                type="date"
                value={form.follow_up_date}
                onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                required
                data-testid="activity-followup-date"
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
              />
            )}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="activity-form-save"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider"
            >
              <Save className="w-4 h-4" strokeWidth={2.2} /> {saving ? "Saving…" : editingId ? "Save changes" : "Add contact"}
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {activities.length === 0 ? (
        <div data-testid="activity-empty" className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          No contacts recorded yet. Click <strong>Add Contact</strong> to record chaplain outreach.
        </div>
      ) : (
        <ol data-testid="activity-list" className="space-y-3">
          {activities.map((a, idx) => (
            <li
              key={a.id}
              data-testid={`activity-item-${idx}`}
              className="rounded-xl border border-slate-200 bg-white p-4 hover:border-[#4A6FA5]/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F0F4F8] text-[#3B597B] text-[11px] font-semibold uppercase tracking-wider">
                      {a.contact_type}
                    </span>
                    <span className="text-xs text-slate-500">{fmtDateTime(a.date, a.time)}</span>
                    {a.follow_up_needed && (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          a.follow_up_date && a.follow_up_date < todayIsoDate()
                            ? "bg-red-50 text-red-800"
                            : "bg-amber-50 text-amber-800"
                        }`}
                      >
                        <AlertCircle className="w-3 h-3" strokeWidth={2.2} />
                        Follow up{a.follow_up_date ? `: ${a.follow_up_date}` : ""}
                      </span>
                    )}
                  </div>
                  {Array.isArray(a.areas_of_support) && a.areas_of_support.length > 0 && (
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`activity-item-${idx}-areas`}>
                      {a.areas_of_support.map((area) => (
                        <span key={area} className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium">
                          {area}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 text-sm text-slate-800">
                    <User className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5 text-slate-400" strokeWidth={2} /> {a.staff_name}
                  </div>
                  {a.note && (
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{a.note}</p>
                  )}
                  {a.updated_at && (
                    <p className="mt-2 text-[11px] text-slate-400">
                      Edited {new Date(a.updated_at).toLocaleString()} by {a.updated_by_name || "—"}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(a)}
                  data-testid={`activity-edit-${idx}`}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-slate-500 hover:text-[#4A6FA5]"
                  aria-label="Edit contact"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={2} /> Edit
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function SummaryTile({ label, value, icon: Icon, testId, small, highlight }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border p-3 ${highlight ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        <Icon className="w-3 h-3" strokeWidth={2.2} /> {label}
      </div>
      <div className={`mt-1 font-heading text-slate-900 tracking-tight ${small ? "text-sm" : "text-2xl"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function FormField({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-600">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
    </label>
  );
}
